import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { BalanceItemWithPayments, BalanceItemWithPrivatePayments } from '../BalanceItem.js';
import { EmailRecipient } from '../email/Email.js';
import { Recipient, Replacement } from '../endpoints/EmailRequest.js';
import { Payment, PrivatePayment } from '../members/Payment.js';
import { RecordCheckboxAnswer } from '../members/records/RecordAnswer.js';
import { RecordCategory } from '../members/records/RecordCategory.js';
import { downgradePaymentMethodV150, PaymentMethod, PaymentMethodHelper, PaymentMethodV150 } from '../PaymentMethod.js';
import { PaymentStatus } from '../PaymentStatus.js';
import { Checkout } from './Checkout.js';
import { Customer } from './Customer.js';
import { TicketPrivate } from './Ticket.js';
import { CheckoutMethodType } from './WebshopMetaData.js';
export var OrderStatusV103;
(function (OrderStatusV103) {
    OrderStatusV103["Created"] = "Created";
    OrderStatusV103["Prepared"] = "Prepared";
    OrderStatusV103["Completed"] = "Completed";
    OrderStatusV103["Canceled"] = "Canceled";
})(OrderStatusV103 || (OrderStatusV103 = {}));
export var OrderStatusV137;
(function (OrderStatusV137) {
    OrderStatusV137["Created"] = "Created";
    OrderStatusV137["Prepared"] = "Prepared";
    OrderStatusV137["Collect"] = "Collect";
    OrderStatusV137["Completed"] = "Completed";
    OrderStatusV137["Canceled"] = "Canceled";
})(OrderStatusV137 || (OrderStatusV137 = {}));
export var OrderStatus;
(function (OrderStatus) {
    OrderStatus["Created"] = "Created";
    OrderStatus["Prepared"] = "Prepared";
    OrderStatus["Collect"] = "Collect";
    OrderStatus["Completed"] = "Completed";
    OrderStatus["Canceled"] = "Canceled";
    OrderStatus["Deleted"] = "Deleted";
})(OrderStatus || (OrderStatus = {}));
export class OrderStatusHelper {
    static getName(status) {
        switch (status) {
            case OrderStatus.Created: return $t(`%1IY`);
            case OrderStatus.Prepared: return $t(`%t7`);
            case OrderStatus.Collect: return $t(`%t8`);
            case OrderStatus.Completed: return $t(`%Gt`);
            case OrderStatus.Canceled: return $t(`%gg`);
            case OrderStatus.Deleted: return $t(`%1FX`);
        }
    }
    static getColor(status) {
        switch (status) {
            case OrderStatus.Completed: return 'success';
            case OrderStatus.Prepared: return 'secundary';
            case OrderStatus.Collect: return 'tertiary';
            case OrderStatus.Canceled: return 'error';
            case OrderStatus.Created: return 'info';
        }
        return 'error';
    }
}
export class OrderData extends Checkout {
    consumerLanguage = 'nl';
    comments = '';
    // Payment method is required
    paymentMethod = PaymentMethod.Unknown;
    get shouldSendPaymentUpdates() {
        return (!this.timeSlot || (this.timeSlot.date.getTime() + 1000 * 60 * 60 * 24) > new Date().getTime());
    }
    get locationName() {
        if (this.checkoutMethod?.type === CheckoutMethodType.Takeout) {
            return this.checkoutMethod.name;
        }
        if (this.checkoutMethod?.type === CheckoutMethodType.OnSite) {
            return this.checkoutMethod.name;
        }
        return this.address?.shortString() ?? $t(`%Gr`);
    }
    /**
     * Delete the personal data associated with an order when you delete an order.
     * We still need the other data (e.g. to inform other clients about a deleted order)
     * And also to match online payments and handle refunds if needed
     */
    removePersonalData() {
        // Clear customer data
        this.customer = Customer.create({});
        // Clear free inputs
        this.fieldAnswers = [];
        for (const item of this.cart.items) {
            item.fieldAnswers = [];
        }
        this.recordAnswers = new Map();
    }
}
__decorate([
    field({ decoder: StringDecoder, version: 129 })
], OrderData.prototype, "consumerLanguage", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 158 })
], OrderData.prototype, "comments", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentMethodV150) }),
    field({
        decoder: new EnumDecoder(PaymentMethod),
        version: 151,
        downgrade: downgradePaymentMethodV150,
    })
], OrderData.prototype, "paymentMethod", void 0);
export class Order extends AutoEncoder {
    id;
    webshopId;
    number = null;
    data = OrderData.create({});
    balanceItems = [];
    /**
     * @deprecated: replaced by balance items
     */
    payment = null;
    createdAt = new Date();
    updatedAt = new Date();
    validAt = null;
    status = OrderStatus.Created;
    get shouldIncludeStock() {
        return this.status !== OrderStatus.Canceled && this.status !== OrderStatus.Deleted;
    }
    get pricePaid() {
        return this.balanceItems.reduce((total, item) => total + item.pricePaid, 0);
    }
    get totalToPay() {
        if (this.status === OrderStatus.Canceled || this.status === OrderStatus.Deleted) {
            return 0;
        }
        return this.data.totalPrice;
    }
    get openBalance() {
        return this.totalToPay - this.pricePaid;
    }
    // used only for inMemoryFilters
    get amountToPay() {
        return this.data.totalPrice - this.pricePaid;
    }
    updatePricePaid() {
        for (const item of this.balanceItems) {
            item.updatePricePaid();
        }
    }
    get payments() {
        return this.balanceItems.flatMap(i => i.payments.map(p => p.payment)).filter(p => p.status !== PaymentStatus.Failed).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    }
    get paidAt() {
        const time = Math.min(new Date(2100, 0, 1).getTime(), ...this.payments.map(p => (p.paidAt ?? new Date(2100, 0, 1)).getTime()));
        return new Date(time);
    }
    getHTMLTable() {
        const allFree = this.data.cart.items.every(i => i.getPriceWithoutDiscounts() === 0);
        if (allFree) {
            let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><thead><tr><th>Artikel</th><th>Aantal</th></tr></thead><tbody>`;
            for (const item of this.data.cart.items) {
                str += `<tr><td><h4>${Formatter.escapeHtml(item.product.name)}</h4>${item.description.length > 0 ? '<p style="white-space: pre-wrap;">' + Formatter.escapeHtml(item.description) + '</p>' : ''}</td><td>${Formatter.escapeHtml(item.formattedAmount ?? '1')}</td></tr>`;
            }
            return str + '</tbody></table>';
        }
        let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><thead><tr><th>Artikel</th><th>Aantal</th></tr></thead><tbody>`;
        for (const item of this.data.cart.items) {
            str += `<tr><td><h4>${Formatter.escapeHtml(item.product.name)}</h4>${item.description.length > 0 ? '<p style="white-space: pre-wrap;">' + Formatter.escapeHtml(item.description) + '</p>' : ''}${'<p style="white-space: pre-wrap;">' + Formatter.escapeHtml(item.getFormattedPriceWithDiscount() || item.getFormattedPriceWithoutDiscount()) + '</p>'}</td><td>${Formatter.escapeHtml(item.formattedAmount ?? '1')}</td></tr>`;
        }
        return str + '</tbody></table>';
    }
    getDetailsHTMLTable(webshop) {
        let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><tbody>`;
        const data = [
            {
                title: $t('%xA'),
                value: '' + (this.number ?? '?'),
            },
            {
                title: ((order) => {
                    if (order.data.checkoutMethod?.type === CheckoutMethodType.Takeout) {
                        return $t(`%Uq`);
                    }
                    if (order.data.checkoutMethod?.type === CheckoutMethodType.OnSite) {
                        return $t(`%8a`);
                    }
                    return $t(`%Us`);
                })(this),
                value: ((order) => {
                    if (order.data.checkoutMethod?.type === CheckoutMethodType.Takeout) {
                        return order.data.checkoutMethod.name;
                    }
                    if (order.data.checkoutMethod?.type === CheckoutMethodType.OnSite) {
                        return order.data.checkoutMethod.name;
                    }
                    return order.data.address?.shortString() ?? '';
                })(this),
            },
            ...((this.data.checkoutMethod?.type === CheckoutMethodType.Takeout || this.data.checkoutMethod?.type === CheckoutMethodType.OnSite) && (this.data.checkoutMethod?.address)
                ? [
                    {
                        title: $t(`%Cn`),
                        value: ((order) => {
                            return order.data.checkoutMethod?.address?.shortString() ?? '';
                        })(this),
                    },
                ]
                : []),
            {
                title: $t(`%7R`),
                value: Formatter.capitalizeFirstLetter(this.data.timeSlot?.dateString() ?? ''),
            },
            {
                title: $t(`%1GD`),
                value: this.data.timeSlot?.timeRangeString() ?? '',
            },
            {
                title: $t(`%1Os`),
                value: this.data.customer.name,
            },
            ...(this.data.customer.phone
                ? [
                    {
                        title: $t(`%18Z`),
                        value: this.data.customer.phone,
                    },
                ]
                : []),
            ...this.data.fieldAnswers.filter(a => a.answer).map(a => ({
                title: a.field.name,
                value: a.answer,
            })),
            ...RecordCategory.sortAnswers(this.data.recordAnswers, webshop.meta.recordCategories).filter(a => !a.isEmpty || a instanceof RecordCheckboxAnswer).map(a => ({
                title: a.settings.name.toString(),
                value: a.stringValue,
            })),
            ...((this.data.paymentMethod !== PaymentMethod.Unknown)
                ? [
                    {
                        title: $t(`%M7`),
                        value: Formatter.capitalizeFirstLetter(PaymentMethodHelper.getName(this.data.paymentMethod)),
                    },
                ]
                : []),
            ...this.data.priceBreakown.map((p) => {
                return {
                    title: p.name,
                    value: Formatter.price(p.price),
                };
            }),
        ];
        for (const replacement of data) {
            if (replacement.value.length === 0) {
                continue;
            }
            str += `<tr><td><h4>${Formatter.escapeHtml(replacement.title)}</h4></td><td>${Formatter.escapeHtml(replacement.value)}</td></tr>`;
        }
        return str + '</tbody></table>';
    }
    getEmailRecipient(organization, webshop) {
        const customer = this.data.customer;
        const email = customer.email.toLowerCase();
        return EmailRecipient.create({
            objectId: this.id,
            firstName: customer.firstName,
            lastName: customer.lastName,
            email,
            replacements: this.getRecipientReplacements(organization, webshop),
        });
    }
    getRecipient(organization, webshop, payment) {
        const order = this;
        const email = order.data.customer.email.toLowerCase();
        return Recipient.create({
            firstName: order.data.customer.firstName,
            lastName: order.data.customer.lastName,
            email,
            replacements: this.getRecipientReplacements(organization, webshop, payment ? [payment] : order.payments),
        });
    }
    getRecipientReplacements(organization, webshop, payments = this.payments) {
        const order = this;
        const succeededTransfers = payments
            .filter(p => p.status === PaymentStatus.Succeeded && p.method === PaymentMethod.Transfer) ?? payments.filter(p => p.method === PaymentMethod.Transfer);
        return [
            Replacement.create({
                token: 'orderUrl',
                value: 'https://' + webshop?.getUrl(organization) + '/order/' + (order.id),
            }),
            Replacement.create({
                token: 'nr',
                value: (order.number ?? '') + '',
            }),
            Replacement.create({
                token: 'orderPrice',
                value: Formatter.price(order.data.totalPrice),
            }),
            Replacement.create({
                token: 'priceToPay',
                value: order.openBalance <= 0 ? '' : Formatter.price(order.openBalance),
            }),
            Replacement.create({
                token: 'paymentMethod',
                value: order.data.paymentMethod,
            }),
            Replacement.create({
                token: 'transferDescription',
                value: succeededTransfers.map(p => p.transferDescription).join(', '),
            }),
            Replacement.create({
                token: 'transferBankAccount',
                value: succeededTransfers
                    .map(p => p.transferSettings?.iban ?? organization.meta.registrationPaymentConfiguration.transferSettings.iban)
                    .filter(iban => !!iban)
                    .join(', '),
            }),
            Replacement.create({
                token: 'transferBankCreditor',
                value: succeededTransfers
                    .map(p => p.transferSettings?.creditor ?? organization.meta.registrationPaymentConfiguration.transferSettings.creditor ?? organization.name)
                    .join(', '),
            }),
            Replacement.create({
                token: 'orderStatus',
                value: OrderStatusHelper.getName(order.status),
            }),
            Replacement.create({
                token: 'orderMethod',
                value: order.data.checkoutMethod?.typeName ?? '',
            }),
            Replacement.create({
                token: 'orderLocation',
                value: order.data.locationName,
            }),
            Replacement.create({
                token: 'orderDate',
                value: order.data.timeSlot?.dateString() ?? '',
            }),
            Replacement.create({
                token: 'orderTime',
                value: order.data.timeSlot?.timeRangeString() ?? '',
            }),
            Replacement.create({
                token: 'orderDetailsTable',
                value: '',
                html: order.getDetailsHTMLTable(webshop),
            }),
            Replacement.create({
                token: 'orderTable',
                value: '',
                html: order.getHTMLTable(),
            }),
            Replacement.create({
                token: 'paymentTable',
                value: '',
                html: order.payments.map(p => p.getHTMLTable()).join(),
            }),
            Replacement.create({
                token: 'organizationName',
                value: organization.name,
            }),
            Replacement.create({
                token: 'webshopName',
                value: webshop.meta.name,
            }),
        ];
    }
}
__decorate([
    field({ decoder: StringDecoder })
], Order.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 140 })
], Order.prototype, "webshopId", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], Order.prototype, "number", void 0);
__decorate([
    field({ decoder: OrderData })
], Order.prototype, "data", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(BalanceItemWithPayments), version: 225 })
], Order.prototype, "balanceItems", void 0);
__decorate([
    field({ decoder: Payment, nullable: true })
], Order.prototype, "payment", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Order.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 107 })
], Order.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Order.prototype, "validAt", void 0);
__decorate([
    field({ decoder: new EnumDecoder(OrderStatusV103), version: 47 })
    // Migrate newer order status .collect in case of older client
    ,
    field({
        decoder: new EnumDecoder(OrderStatusV137),
        version: 104,
        upgrade: (old) => {
            return old;
        },
        downgrade: (n) => {
            if (n === OrderStatusV137.Collect) {
                // Map to other status
                return OrderStatusV103.Prepared;
            }
            return n;
        },
    }),
    field({
        decoder: new EnumDecoder(OrderStatus),
        version: 138,
        upgrade: (old) => {
            return old;
        },
        downgrade: (n) => {
            if (n === OrderStatus.Deleted) {
                // Map to other status
                return OrderStatusV137.Canceled;
            }
            return n;
        },
    })
], Order.prototype, "status", void 0);
export class PrivateOrder extends Order {
    /**
     * @deprecated
     */
    payment = null;
    balanceItems = [];
    get payments() {
        return this.balanceItems.flatMap(i => i.payments.map(p => p.payment)).filter(p => p.status !== PaymentStatus.Failed).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    }
}
__decorate([
    field({ decoder: PrivatePayment, nullable: true })
], PrivateOrder.prototype, "payment", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(BalanceItemWithPrivatePayments), nullable: true, version: 225 })
], PrivateOrder.prototype, "balanceItems", void 0);
export class PrivateOrderWithTickets extends PrivateOrder {
    tickets = [];
    get ticketScanStatus() {
        const scanned = this.tickets.filter(t => t.scannedAt).length;
        const total = this.tickets.length;
        if (scanned === 0) {
            return 'none';
        }
        if (scanned === total) {
            return 'all';
        }
        return 'partial';
    }
    /**
     * Date first ticket was scanned at.
     */
    get ticketScannedAt() {
        return this.tickets.reduce((acc, ticket) => {
            if (ticket.scannedAt && ticket.scannedAt < acc) {
                return ticket.scannedAt;
            }
            return acc;
        }, new Date(2222, 0, 1));
    }
    /**
     * Adds or removes tickets as appropriate
     */
    addTickets(tickets) {
        for (const ticket of tickets) {
            if (ticket.orderId === this.id) {
                if (ticket.deletedAt) {
                    const existingIndex = this.tickets.findIndex(t => t.id === ticket.id);
                    if (existingIndex !== -1) {
                        this.tickets.splice(existingIndex, 1);
                    }
                }
                else {
                    const existing = this.tickets.find(t => t.id === ticket.id);
                    if (existing) {
                        existing.set(ticket);
                    }
                    else {
                        this.tickets.push(ticket);
                    }
                }
            }
        }
    }
    addTicketPatches(patches) {
        PrivateOrderWithTickets.addTicketPatches([this], patches);
    }
    static addTicketPatches(orders, patches) {
        mainLoop: for (const patch of patches) {
            for (const order of orders) {
                for (const ticket of order.tickets) {
                    if (ticket.id === patch.id) {
                        ticket.set(ticket.patch(patch));
                        continue mainLoop;
                    }
                }
            }
        }
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(TicketPrivate) })
], PrivateOrderWithTickets.prototype, "tickets", void 0);
export class OrderResponse extends AutoEncoder {
    paymentUrl = null;
    /**
     * Data to put in a QR-code
     */
    paymentQRCode = null;
    order;
}
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], OrderResponse.prototype, "paymentUrl", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, optional: true })
], OrderResponse.prototype, "paymentQRCode", void 0);
__decorate([
    field({ decoder: Order })
], OrderResponse.prototype, "order", void 0);
//# sourceMappingURL=Order.js.map